import { ref, computed, watch, h, onMounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
    NDataTable,
    NInputNumber,
    NInput,
    NButton,
    NDatePicker,
    NSelect,
    NRadioGroup,
    NRadioButton,
    FormInst,
    FormRules,
    useMessage,
    useNotification,
    useDialog,
} from "naive-ui";
import { getCallable, makeCallApiParam } from "@/config/env";
import * as common from "@/assets/common/common";
import * as constant from "@/constants/constant";
import { Product } from "@/model/product";
import { useActionDialog } from "@/composables/useDialog"; // useActionDialog import 추가
import { Organization, OrganizationType } from "@/model/organization";
import { Order, OrderItem } from "@/model/order";

export function getQuotationStatusString(status: string) {
    if (status === constant.quotationStatus.PENDING) {
        return "수락 대기중";
    } else if (status === constant.quotationStatus.APPROVED) {
        return "수락 됨";
    } else {
        return "알수 없음";
    }
}

export function getOrderStatusString(status: string) {
    if (status === constant.orderStatus.PENDING) {
        return "견적 승인 대기 중";
    } else if (status === constant.orderStatus.APPROVED) {
        return "진단검사 정보입력 대기 중";
    } else if (status === constant.orderStatus.APPROVED_TEMP_SAVE) {
        return "진단검사 정보입력 임시저장 됨";
    } else if (status === constant.orderStatus.READY) {
        return "진단검사 실시 대기 중";
    } else if (status === constant.orderStatus.ING) {
        return "진단검사 실시 중";
    } else if (status === constant.orderStatus.REJECTED) {
        return "진담검사 승인거절 됨";
    } else if (status === constant.orderStatus.CANCELED) {
        return "지단검사 취소 됨";
    } else {
        return "미정";
    }
}

export function getProjectStatusString(status: string) {
    if (status === constant.projectStatus.PENDING) {
        return "진단검사 실시 대기 중";
    } else if (status === constant.projectStatus.ING) {
        return "진단검사 실시 중";
    } else if (status === constant.projectStatus.COMPLETED) {
        return "진단검사 완료";
    } else if (status === constant.projectStatus.CANCELED) {
        return "진단검사 취소";
    } else {
        return "미정";
    }
}

interface UseOrderFormOptions {
    isEdit: boolean;
    orderSK?: string;
    orgSK?: string;
    cancelRouteName: string;
    onSuccessRedirect: string;
    onErrorNotificationTitle: string;
    submissionCallable: (payload: any) => Promise<any>;
}

export function useOrderForm(options: UseOrderFormOptions) {
    const router = useRouter();
    const route = useRoute();
    const message = useMessage();
    const notification = useNotification();
    const dialog = useDialog();

    const pageLoading = ref(true);
    const submitLoading = ref(false);
    const requestFormRef = ref<FormInst | null>(null);

    const prodSet = ref<Product[]>([]);
    const surveySet = ref<Product[]>([]);
    const coachSet = ref<Product[]>([]);

    const selectedCoachSKsForBoth = ref<string[]>([]);
    const orgType = ref<OrganizationType>(OrganizationType.ElementarySchool);
    const selectedOrgType = ref<OrganizationType>(OrganizationType.ElementarySchool);

    const formModel = ref<
        Order & {
            selectedSurveySK?: string | null;
            test_due_date?: string | null;
            coaching_due_date?: string | null;
        }
    >({
        order_id: "",
        SK: "",
        name: "",
        cc: "",
        receiver_email: "",
        client: null,
        payment_method: {
            bank_date: null,
            bank_name: "우리은행 : 1005-104-783814, 예금주:주식회사코코랩",
            deposit_name: "",
            type: "bank_deposit",
        },
        products: [],
        status: "",
        totalPrice: 0,
        type: constant.orderType.BODY,
        selectedSurveySK: null,
        test_due_date: null,
        coaching_due_date: null,
    });

    const getProductsCallable = getCallable("getProducts");
    const getOrgInfoCallable = getCallable("getOrgInfo");
    const getOrderDetailsCallable = getCallable("getOrder");

    const totalPrice = computed(() => {
        return formModel.value.products.reduce((sum, product) => {
            const unit_price = product.unit_price || 0;
            const quantity = product.quantity || 0;
            product.supply_price = Number(unit_price) * quantity;
            return sum + Number(unit_price) * quantity;
        }, 0);
    });

    const availableProducts = ref<Array<{ label: string; value: string }>>([]);

    const baseFormRules: FormRules = {
        name: [
            {
                required: true,
                message: "견적서명을 입력해주세요.",
                trigger: ["input", "blur"],
            },
        ],
        "client.dept": [
            {
                required: true,
                message: "담당부서명을 입력해주세요.",
                trigger: ["input", "blur"],
            },
        ],
        "client.phoneNumber": [
            {
                required: true,
                message: "전화번호를 입력해주세요.",
                trigger: ["input", "blur"],
            },
        ],
        "client.deptPersonName": [
            {
                required: true,
                message: "담당자 성명을 입력해주세요.",
                trigger: ["input", "blur"],
            },
        ],
        "client.deptPersonPhoneNumber": [
            {
                required: true,
                message: "핸드폰번호를 입력해주세요.",
                trigger: ["input", "blur"],
            },
        ],
        "client.email": [
            {
                required: true,
                message: "이메일을 입력해주세요.",
                trigger: ["input", "blur"],
            },
            {
                type: "email",
                message: "올바른 이메일 형식이 아닙니다.",
                trigger: ["input", "blur"],
            },
        ],
        unitPriceRule: [
            {
                required: true,
                type: "number",
                message: "단가를 입력해주세요.",
                trigger: ["input", "blur"],
                min: 1,
            },
        ],
        // Product quantity rule (existing)
        quantityRule: [
            {
                required: true,
                type: "number",
                message: "수량을 입력해주세요.",
                trigger: ["input", "blur"],
                min: 1,
            },
        ],

        // Test and Coaching due dates
        test_due_date: [
            {
                required: true,
                message: "검사실시 예정일을 선택해주세요.",
                trigger: "change",
            },
        ],
        // coaching_due_date: [{ required: true, message: "상담코칭 예정일을 선택해주세요.", trigger: "change" }],
    };

    const newFormRules: FormRules = {
        ...baseFormRules,
        selectedSurveySK: [
            {
                required: true,
                message: "검사 유형을 선택해주세요.",
                trigger: "change",
            },
        ],
        "payment_method.bank_date": [
            {
                required: true,
                message: "입금예정일을 선택해주세요.",
                trigger: "change",
            },
        ],
    };

    const editFormRules: FormRules = {
        ...baseFormRules,
    };

    async function fetchOrganizationInfo(orgSK: string): Promise<boolean> {
        try {
            const result = await getOrgInfoCallable({ SK: orgSK });
            const data = common.getItemFromFunctionResult(result) as Organization;
            if (data && data.name) {
                formModel.value.client = data;
                formModel.value.client.uid = orgSK;
                return true;
            } else {
                console.warn(`기관 정보 로드 실패 (SK: ${orgSK})`);
                return false;
            }
        } catch (error) {
            console.error("기관 정보 로딩 실패:", error);
            notification.error({
                title: "기관 정보 로딩 실패",
                description: `${error || "기관 정보를 불러오는 중 오류가 발생했습니다."}`,
                duration: 3000,
            });
            return false;
        }
    }
    function isUniversity(item: OrderItem): boolean {
        if (
            item.type == "putt_u" ||
            item.type == "tec" ||
            item.type == "coach_putt_u" ||
            item.type == "coach_tec" ||
            item.type == "coach_putt_tec"
        ) {
            return true;
        } else {
            return false;
        }
    }
    let prodSetListCache: Product[][] | null = null;
    let prodInfoList: Product[] | null = null;

    async function getProductSetList(): Promise<Product[][]> {
        if (prodInfoList && prodInfoList.length > 0 && prodSetListCache) {
            return prodSetListCache;
        }

        const result = await getProductsCallable();
        prodInfoList = common.getItemListFromFunctionResult(result) as Product[];
        const typeMap = new Map();
        for (const prodInfo of prodInfoList) {
            if (prodInfo.type && prodInfo.SK) typeMap.set(prodInfo.type, prodInfo);
        }
        const newProdSetList: Product[][] = [
            [
                typeMap.get("putt_e"),
                typeMap.get("tocc_e"),
                typeMap.get("coach_putt_e"),
                typeMap.get("coach_tocc_e"),
                typeMap.get("coach_putt_tocc_e"),
            ],
            [
                typeMap.get("putt_m"),
                typeMap.get("tocc_h"),
                typeMap.get("coach_putt_m"),
                typeMap.get("coach_tocc_m"),
                typeMap.get("coach_putt_tocc_m"),
            ],
            [
                typeMap.get("putt_h"),
                typeMap.get("tocc_h"),
                typeMap.get("coach_putt_h"),
                typeMap.get("coach_tocc_h"),
                typeMap.get("coach_putt_tocc_h"),
            ],
            [
                typeMap.get("putt_u"),
                typeMap.get("tec"),
                typeMap.get("coach_putt_u"),
                typeMap.get("coach_tec"),
                typeMap.get("coach_putt_tec"),
            ],
        ];

        prodSetListCache = newProdSetList;
        return prodSetListCache;
    }

    async function setProdItems() {
        surveySet.value = [];
        coachSet.value = [];
        const surveyBoth = {
            SK: "B",
            name: "",
            price: 0,
        };
        const fullProdSet = await getProductSetList();
        if (selectedOrgType.value === OrganizationType.ElementarySchool) {
            prodSet.value = fullProdSet[0];
            surveyBoth.name = "PUTT2.0_초등학생용‧TOCC_아동용(고학년)";
        } else if (selectedOrgType.value === OrganizationType.MiddleSchool) {
            prodSet.value = fullProdSet[1];
            surveyBoth.name = "PUTT2.0_중학생용‧TOCC_청소년용";
        } else if (selectedOrgType.value === OrganizationType.HighSchool) {
            prodSet.value = fullProdSet[2];
            surveyBoth.name = "PUTT2.0_고등학생용‧TOCC_청소년용";
        } else if (selectedOrgType.value === OrganizationType.University) {
            prodSet.value = fullProdSet[3];
            surveyBoth.name = "PUTT2.0_대학생용‧TEC_전문가용";
        }
        for (const product of prodSet.value) {
            if (product.SK?.startsWith("personal")) {
                surveySet.value.push(product);
            } else {
                coachSet.value.push(product);
            }
        }
        surveySet.value.push(surveyBoth);
    }

    const updateRequestedProducts = () => {
        const oldQuantities = new Map(formModel.value.products.map((p) => [p.prod_id, p.quantity]));
        const newProducts: OrderItem[] = [];

        if (!formModel.value.selectedSurveySK) {
            formModel.value.products = [];
            return;
        }

        const selectedSurveyProduct = surveySet.value.find((s) => s.SK === formModel.value.selectedSurveySK);
        if (selectedSurveyProduct) {
            coachSet.value = [];
            if (selectedSurveyProduct.type?.startsWith("putt")) {
                for (const product of prodSet.value) {
                    if (
                        !product.SK?.startsWith("personal") &&
                        product.type?.startsWith("coach_putt") &&
                        !product.type?.startsWith("coach_putt_tocc") &&
                        !product.type?.startsWith("coach_putt_tec")
                    ) {
                        coachSet.value.push(product);
                    }
                }
            } else if (selectedSurveyProduct.type?.startsWith("tocc")) {
                for (const product of prodSet.value) {
                    if (!product.SK?.startsWith("personal") && product.type?.startsWith("coach_tocc")) {
                        coachSet.value.push(product);
                    }
                }
            } else if (selectedSurveyProduct.type?.startsWith("tec")) {
                for (const product of prodSet.value) {
                    if (!product.SK?.startsWith("personal") && product.type?.startsWith("coach_tec")) {
                        coachSet.value.push(product);
                    }
                }
            } else {
                for (const product of prodSet.value) {
                    if (!product.SK?.startsWith("personal")) {
                        coachSet.value.push(product);
                    }
                }
            }

            if (formModel.value.selectedSurveySK === "B") {
                for (const surveyProduct of surveySet.value) {
                    if (surveyProduct.SK === "B") continue;
                    newProducts.push({
                        prod_id: surveyProduct.SK!,
                        name: surveyProduct.name,
                        price: surveyProduct.price,
                        type: surveyProduct.type,
                        unit_price: surveyProduct.price,
                        template_form_id: surveyProduct.template_form_id, // Ensure this property exists on surveyProduct or handle if optional
                        quantity: oldQuantities.get(surveyProduct.SK!) || 1,
                        supply_price: surveyProduct.price * 1,
                        notes: "",
                    });
                }
            } else {
                newProducts.push({
                    prod_id: selectedSurveyProduct.SK!,
                    name: selectedSurveyProduct.name,
                    price: selectedSurveyProduct.price,
                    type: selectedSurveyProduct.type,
                    unit_price: selectedSurveyProduct.price,
                    template_form_id: selectedSurveyProduct.template_form_id, // Ensure this property exists or handle if optional
                    quantity: oldQuantities.get(selectedSurveyProduct.SK!) || 1,
                    supply_price: selectedSurveyProduct.price * 1,
                    notes: "",
                });
            }
        }

        // if (formModel.value.selectedSurveySK === "B") {
        selectedCoachSKsForBoth.value.forEach((coachSK) => {
            const coachProduct = coachSet.value.find((c) => c.SK === coachSK);
            if (coachProduct) {
                newProducts.push({
                    prod_id: coachProduct.SK!,
                    price: coachProduct.price,
                    unit_price: coachProduct.price,
                    name: coachProduct.name,
                    quantity: oldQuantities.get(coachProduct.SK!) || 1,
                    supply_price: coachProduct.price * 1,
                    notes: "",
                });
            }
        });
        // } else {
        // const originalSurveys = surveySet.value.filter((s) => s.SK !== "B");
        // const originalCoaches = coachSet.value.filter((c) => c.SK !== "B");
        // const surveyIndex = originalSurveys.findIndex(
        //   (s) => s.SK === formModel.value.selectedSurveySK
        // );

        // if (surveyIndex !== -1 && surveyIndex < originalCoaches.length) {
        //   const correspondingCoach = originalCoaches[surveyIndex];
        //   if (correspondingCoach) {
        //     if (!newProducts.find((p) => p.prod_id === correspondingCoach.SK)) {
        //       newProducts.push({
        //         prod_id: correspondingCoach.SK!,
        //         name: correspondingCoach.name,
        //         price: correspondingCoach.price,
        //         unit_price: correspondingCoach.price,
        //         quantity: oldQuantities.get(correspondingCoach.SK!) || 1,
        //         supply_price: correspondingCoach.price * 1,
        //         notes: "",
        //       });
        //     }
        //   }
        // }
        // }
        formModel.value.products = newProducts;
    };

    watch(
        () => formModel.value.selectedSurveySK,
        (newVal) => {
            //   if (newVal !== "B" && newVal !== undefined) {
            // Check newVal is not undefined if it can be
            selectedCoachSKsForBoth.value = []; // Reset checkboxes if not "Both" survey
            //   }
            updateRequestedProducts();
        }
    );

    watch(
        selectedCoachSKsForBoth,
        () => {
            //   if (formModel.value.selectedSurveySK === "B") {
            updateRequestedProducts();
            //   }
        },
        { deep: true }
    );

    watch(selectedOrgType, async (newType, oldType) => {
        if (newType && newType !== oldType) {
            pageLoading.value = true;
            await setProdItems();
            // 기관 유형이 변경되면 선택된 검사 및 코칭 상품 초기화
            formModel.value.selectedSurveySK = null;
            selectedCoachSKsForBoth.value = [];
            formModel.value.products = []; // 요청 상품 목록도 초기화
            pageLoading.value = false;
        }
    });

    async function handleSubmitOrder() {
        try {
            await requestFormRef.value?.validate();
            if (formModel.value.products.length === 0) {
                message.error("요청할 상품을 하나 이상 선택해주세요.");
                return;
            }
        } catch (errors) {
            message.error("입력 내용을 확인해주세요.");
            return;
        }

        submitLoading.value = true;
        try {
            // 제출 페이로드 구성
            formModel.value.receiver_email = formModel.value.client!.email;
            const payload = {
                ...formModel.value,
                ...(options.isEdit && { SK: formModel.value.SK }),
            };
            payload.totalPrice = totalPrice.value;
            if (options.isEdit && !payload.SK) {
                throw new Error("수정할 견적서의 ID가 없습니다.");
            }
            let param = makeCallApiParam(options.isEdit ? "updateOrder" : "createOrder", payload);
            const result = await options.submissionCallable(param);
            if (common.evalFunctionResult(result)) {
                notification.success({
                    title: "성공",
                    description: options.isEdit ? "견적서 수정이 완료되었습니다." : "견적서 신청이 완료되었습니다.",
                    duration: 3000,
                });
                router.push({ name: options.onSuccessRedirect });
            } else {
                throw new Error(common.getResultError(result) || "오류가 발생했습니다.");
            }
        } catch (error: any) {
            console.error("제출 실패:", error);
            notification.error({
                title: options.onErrorNotificationTitle,
                description: common.getResultError(error) || "알 수 없는 오류가 발생했습니다.",
                duration: 3000,
            });
        } finally {
            submitLoading.value = false;
        }
    }

    function handleCancelEdit() {
        router.push({ name: options.cancelRouteName });
    }

    async function setupForm() {
        pageLoading.value = true;
        try {
            let determinedOrgType: OrganizationType | undefined = selectedOrgType.value; // 현재 값으로 초기화

            if (options.isEdit && options.orderSK) {
                const result = await getOrderDetailsCallable({ SK: options.orderSK });
                const data = common.getItemFromFunctionResult(result) as Order;
                if (data && data.client) {
                    formModel.value = { ...formModel.value, ...data };
                    formModel.value.products = data.products ? [...data.products] : [];
                    orgType.value = formModel.value.client!.type! as OrganizationType;
                    determinedOrgType =
                        orgType.value === OrganizationType.Etc ? OrganizationType.ElementarySchool : orgType.value;
                } else {
                    throw new Error("견적 정보를 찾을 수 없습니다.");
                }
            } else if (!options.isEdit && options.orgSK) {
                const res = await fetchOrganizationInfo(options.orgSK);
                if (res && formModel.value.client) {
                    formModel.value.payment_method.bank_name = "우리은행 : 1005-104-783814, 예금주:주식회사코코랩";
                    formModel.value.payment_method.deposit_name = formModel.value.client!.name!;
                    formModel.value.payment_method.type = "bank_deposit";
                    formModel.value.products = [];
                    formModel.value.totalPrice = 0;
                    orgType.value = formModel.value.client!.type! as OrganizationType;
                    determinedOrgType =
                        orgType.value === OrganizationType.Etc ? OrganizationType.ElementarySchool : orgType.value;
                }
            } else {
                const errorMessage = options.isEdit
                    ? "수정할 견적서 ID가 없습니다."
                    : "견적 등록 대상 기관 ID가 없습니다.";
                notification.error({
                    title: "오류",
                    description: errorMessage,
                    duration: 3000,
                });
                router.push({ name: options.cancelRouteName });
                return;
            }
            if (selectedOrgType.value !== determinedOrgType) {
                selectedOrgType.value = determinedOrgType;
            } else {
                await setProdItems();
            }
        } catch (error: any) {
            console.error("폼 초기 설정 실패:", error);
            notification.error({
                title: "초기 설정 오류",
                description: common.getResultError(error) || "폼 데이터를 불러오는 중 오류가 발생했습니다.",
                duration: 3000,
            });
            router.push({ name: options.cancelRouteName });
        } finally {
            pageLoading.value = false;
        }
    }

    return {
        // 상태
        pageLoading,
        submitLoading,
        requestFormRef,
        formModel,

        // 계산된 속성
        orgType,
        selectedOrgType,
        surveySet,
        coachSet,
        selectedCoachSKsForBoth,
        totalPrice,

        isUniversity,
        newFormRules,
        editFormRules,

        // 함수
        handleSubmitOrder,
        handleCancelEdit,
        setupForm,
        fetchOrganizationInfo,
    };
}

export function useOrderApi() {
    const router = useRouter();
    const message = useMessage();
    const { show: showActionDialog } = useActionDialog();
    const deleteOrderCallable = getCallable("deleteOrder");
    const isLoading = ref(false);
    async function deleteOrder(orderSK: string): Promise<boolean> {
        return new Promise((resolve) => {
            if (!orderSK) {
                message.error("삭제할 견적 정보가 올바르지 않습니다.");
                resolve(false);
                return;
            }

            showActionDialog({
                title: "견적 삭제",
                content: "이 견적을 정말 삭제하시겠습니까? 삭제된 견적은 복구할 수 없습니다.",
                positiveText: "삭제",
                negativeText: "취소",
                positiveButtonProps: { type: "error" },
                confirmButtonLoadingText: "삭제 중...",
                onConfirm: async () => {
                    try {
                        const result = await deleteOrderCallable({ SK: orderSK });
                        if (common.evalFunctionResult(result)) {
                            message.success("견적이 성공적으로 삭제되었습니다.");
                            resolve(true);
                        } else {
                            message.error(common.getResultError(result) || "견적 삭제 중 오류가 발생했습니다.");
                            throw new Error(common.getResultError(result) || "견적 삭제 중 오류가 발생했습니다.");
                        }
                    } catch (error: any) {
                        console.error("견적 삭제 API 호출 실패:", error);
                        resolve(false);
                        throw error;
                    }
                },
            });
        });
    }
    return {
        deleteOrder,
    };
}
